import React, { useState } from 'react';
import { VideoPlayer } from './components/VideoPlayer';
import { ChapterSummary } from './components/ChapterSummary';
import { NotesEditor } from './components/NotesEditor';
import { Header } from './components/Header';
import { InsightView } from './components/InsightView';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Play, BookOpen, Loader2, Sparkles, Scissors, ArrowRight, Link2 } from 'lucide-react';
import { geminiService, Short, Chapter as GeminiChapter, LanguageLearning } from './services/geminiService';
import { ShortsStudio } from './components/shorts/ShortsStudio';
import { EnglishLearningView } from './components/EnglishLearningView';
import { insightService, VideoInsightAnalysis } from './services/insightService';
import { youtubeService } from './services/youtubeService';

// Use GeminiChapter imported from geminiService

interface VideoData {
  videoId: string;
  title: string;
  description: string;
  duration: number;
  chapters: GeminiChapter[];
  tools?: string[];
  materials?: string[];
  shorts?: Short[];
  languageLearning?: LanguageLearning;
}

interface Note {
  id: string;
  timestamp: string;
  title: string;
  content: string;
  chapterId?: string;
  createdAt: string;
}

const devShortsDemo: VideoData | null = process.env.NODE_ENV === 'development' && new URLSearchParams(window.location.search).has('shortsDemo') ? {
  videoId: 'y96MO1TXojo',
  title: '[Playlist] Chillhop Focus Mix - Espresso Love | 2h48m by Floasis',
  description: '쇼츠 편집기 로컬 검증용 데이터',
  duration: 10080,
  chapters: [],
  shorts: [
    { id: 'short-1', title: '삶이 버벅거릴 때 그루브 찾아주는 곡', startTime: 84, endTime: 114, duration: 30, script: '잠깐 멈추고, 호흡을 고른 뒤 다시 시작해도 괜찮아요.', hookType: '감성 훅', thumbnailUrl: 'https://img.youtube.com/vi/y96MO1TXojo/hqdefault.jpg' },
    { id: 'short-2', title: '집중이 필요할 때 듣는 칠홉', startTime: 420, endTime: 450, duration: 30, script: '해야 할 일은 줄이고, 지금 이 한 가지에만 집중하세요.', hookType: '집중 훅', thumbnailUrl: 'https://img.youtube.com/vi/y96MO1TXojo/hqdefault.jpg' }
  ]
} : null;

export default function App() {
  const [videoData, setVideoData] = useState<VideoData | null>(devShortsDemo);
  const [insightData, setInsightData] = useState<VideoInsightAnalysis | null>(null);
  const [insightError, setInsightError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isAnalyzingInsight, setIsAnalyzingInsight] = useState<boolean>(false);
  const [currentChapter, setCurrentChapter] = useState<string>('');
  const [playbackTime, setPlaybackTime] = useState(0);
  const [notes, setNotes] = useState<Note[]>([]);
  const [, setViewMode] = useState<'chapters' | 'insights'>('chapters');
  const [appMode, setAppMode] = useState<'summary' | 'shorts'>(devShortsDemo ? 'shorts' : 'summary');
  const [error, setError] = useState<string | null>(null);

  // 실제 AI 서비스를 사용한 영상 분석
  const analyzeVideo = async (videoId: string): Promise<VideoData> => {
    const videoInfo = await youtubeService.getVideoInfo(videoId);
    const transcript = await youtubeService.getVideoTranscript(videoId);
    const analysis = await geminiService.analyzeVideoFromTranscript(
      videoId,
      videoInfo.title,
      transcript,
      videoInfo.duration,
      videoInfo.thumbnail
    );

    if (!analysis.chapters?.length || analysis.description.includes('오류')) {
      throw new Error('영상 내용을 분석하지 못했습니다. 자막이 있는 공개 영상으로 다시 시도해주세요.');
    }

    return {
      videoId,
      title: analysis.title,
      description: analysis.description,
      duration: analysis.duration,
      chapters: analysis.chapters,
      tools: analysis.tools,
      materials: analysis.materials,
      shorts: analysis.shorts,
      languageLearning: analysis.languageLearning,
    };
  };

  const analyzeVideoForInsights = async (videoId: string) => {
    try {
      const videoInfo = await youtubeService.getVideoInfo(videoId);

      const analysis = await insightService.analyzeVideoForInsights(
        videoId,
        videoInfo.title,
        '',
        videoInfo.duration,
        videoInfo
      );

      return analysis;
    } catch (error) {
      console.error('인사이트 분석 에러:', error);
      throw error;
    }
  };

  const handleVideoAnalysis = async (url: string) => {
    setIsAnalyzing(true);
    setError(null);
    try {
      const videoId = youtubeService.extractVideoId(url);
      if (!videoId) {
        throw new Error('유효하지 않은 YouTube URL입니다.');
      }

      const data = await analyzeVideo(videoId);
      setVideoData(data);
      setCurrentChapter(data.chapters[0]?.id || '');
      setPlaybackTime(data.chapters[0]?.startTime || 0);
    } catch (error) {
      console.error('영상 분석 중 오류 발생:', error);
      setError(error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleJumpToTime = (startTime: number) => {
    setPlaybackTime(startTime);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddNote = (newNote: Omit<Note, 'id' | 'createdAt'>) => {
    const note: Note = {
      ...newNote,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    setNotes((prev: Note[]) => [...prev, note]);
  };

  const handleDeleteNote = (noteId: string) => {
    setNotes((prev: Note[]) => prev.filter((note: Note) => note.id !== noteId));
  };

  const handleInsightAnalysis = async () => {
    if (!videoData) return;

    setIsAnalyzingInsight(true);
    setInsightError(null);
    try {
      const insights = await analyzeVideoForInsights(videoData.videoId);
      setInsightData(insights);
      setViewMode('insights');
    } catch (error) {
      console.error('인사이트 분석 중 오류 발생:', error);
      setInsightError(error instanceof Error ? error.message : '인사이트 분석에 실패했습니다.');
    } finally {
      setIsAnalyzingInsight(false);
    }
  };

  const formatTime = (seconds: number | string): string => {
    const s = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
    if (isNaN(s)) return '0:00';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (videoData && appMode === 'shorts' && videoData.shorts && videoData.shorts.length > 0) {
    return (
      <ShortsStudio 
        videoTitle={videoData.title} 
        videoId={videoData.videoId}
        shorts={videoData.shorts} 
        onBack={() => setVideoData(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#17141f]">
      <Header />

      <main className="mx-auto max-w-[1200px] px-5 py-8 sm:px-8">
        {!videoData ? (
          <div className="landing-shell -mx-5 -mt-8 min-h-[calc(100vh-80px)] sm:-mx-8">
            <div id="start" className="relative flex min-h-[640px] flex-col items-center justify-center overflow-hidden px-5 py-20 text-center">
              <div className="hero-orb hero-orb-one" /><div className="hero-orb hero-orb-two" />
              <div className="relative mb-7 inline-flex items-center gap-2 rounded-full border border-[#dcd5ff] bg-white/80 px-4 py-2 text-[11px] font-black tracking-[.14em] text-[#5b3ee4] shadow-sm backdrop-blur"><Sparkles className="h-3.5 w-3.5" /> WATCH LESS · REMEMBER MORE</div>
              <h1 className="relative max-w-4xl text-5xl font-black leading-[1.06] tracking-[-0.055em] text-[#17141f] md:text-7xl">
                관심 있는 영상이<br />
                <span className="gradient-text">오래 남는 지식으로.</span>
              </h1>

              <p className="relative mb-9 mt-7 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
                영상의 핵심을 파악하고, 나에게 필요한 영어 표현을 자연스럽게 익히고,<br className="hidden sm:block" /> 잊을 때쯤 다시 만나세요. 집중은 짧게, 학습은 길게 이어집니다.
              </p>

              <div className="relative mb-5 flex rounded-full border border-[#e3ded6] bg-white/80 p-1 shadow-sm backdrop-blur">
                <button
                  onClick={() => setAppMode('shorts')}
                    className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${
                    appMode === 'shorts' 
                      ? 'bg-[#17141f] text-white shadow-md' : 'text-slate-500 hover:text-slate-950'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Scissors className="w-4 h-4" /> 숏폼 자동 생성
                  </span>
                </button>
                <button
                  onClick={() => setAppMode('summary')}
                    className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${
                    appMode === 'summary' 
                      ? 'bg-[#17141f] text-white shadow-md' : 'text-slate-500 hover:text-slate-950'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> 영상 요약 분석
                  </span>
                </button>
              </div>

              <div className="relative w-full max-w-2xl rounded-[24px] border border-[#e8e4dc] bg-white p-3 shadow-[0_24px_70px_-32px_rgba(43,34,70,.32)]">
                <form onSubmit={(e: React.FormEvent) => { e.preventDefault(); const input = (e.target as HTMLFormElement).elements.namedItem('url') as HTMLInputElement; if (input.value) handleVideoAnalysis(input.value); }}>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1"><Link2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      name="url"
                      type="url"
                      placeholder="YouTube URL"
                      className="h-14 w-full rounded-[15px] border-0 bg-[#f4f2ed] pl-12 pr-5 text-base outline-none transition focus:bg-white focus:ring-4 focus:ring-[#6d4aff]/15 placeholder:text-slate-400"
                      disabled={isAnalyzing}
                    /></div>
                    <button
                      type="submit"
                      disabled={isAnalyzing}
                      className="flex h-14 items-center justify-center gap-2 rounded-[15px] bg-[#6d4aff] px-7 text-sm font-bold text-white shadow-lg shadow-[#6d4aff]/20 transition hover:bg-[#5838e8] disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {isAnalyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> 분석 중...</> : <>분석 시작 <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </form>

                {isAnalyzing ? (
                  <div className="mt-3 rounded-2xl bg-[#f0edff] px-4 py-3 text-left" role="status" aria-live="polite">
                    <div className="flex items-center gap-3">
                      <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6d4aff] opacity-50"/><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#6d4aff]"/></span>
                      <div><p className="text-sm font-bold text-[#3c287f]">영상 전체의 음성과 화면을 읽고 있어요</p><p className="mt-0.5 text-xs text-[#6d5aa5]">16분 영상 기준 약 1~2분 걸려요. 탭을 닫지 말고 잠시만 기다려주세요.</p></div>
                    </div>
                  </div>
                ) : null}
                {error && <p role="alert" className="mt-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-left text-sm font-medium text-red-700">{error}</p>}
                <div className="mt-4">
                  <button
                    onClick={() => handleVideoAnalysis("https://youtu.be/32vqaJa90kw?si=cBZtSDF5VvK2oPhF")}
                    disabled={isAnalyzing}
                    className="text-xs font-bold text-slate-400 transition-colors hover:text-[#6d4aff] disabled:opacity-50"
                  >
                    앱 마케팅 샘플로 테스트
                  </button>
                </div>
              </div>
            </div>

            <div id="features" className="mt-auto border-t border-[#e8e4dc] bg-[#fbfaf7]">
              <div className="max-w-6xl mx-auto px-4 py-20">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="rounded-[22px] border border-[#e8e4dc] bg-white p-7">
                    <p className="text-sm text-neutral-400 mb-3 font-medium tracking-wider uppercase">01</p>
                    <h3 className="mb-3 text-xl font-extrabold text-[#17141f]">맥락으로 이해</h3>
                    <p className="text-neutral-500 text-sm leading-relaxed">
                      영상을 의미 단위로 나누고, 나의 목표에 필요한 핵심만 남깁니다.
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-[#e8e4dc] bg-white p-7">
                    <p className="text-sm text-neutral-400 mb-3 font-medium tracking-wider uppercase">02</p>
                    <h3 className="mb-3 text-xl font-extrabold text-[#17141f]">문맥으로 영어 흡수</h3>
                    <p className="text-neutral-500 text-sm leading-relaxed">
                      관심 있는 주제에서 실제로 쓰인 표현을 뜻과 활용 맥락과 함께 익힙니다.
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-[#e8e4dc] bg-white p-7">
                    <p className="text-sm text-neutral-400 mb-3 font-medium tracking-wider uppercase">03</p>
                    <h3 className="mb-3 text-xl font-extrabold text-[#17141f]">잊기 전에 다시 만나기</h3>
                    <p className="text-neutral-500 text-sm leading-relaxed">
                      짧은 회상과 간격 복습으로 내용과 영어를 나의 지식으로 만듭니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-7 pb-16 pt-2 lg:grid-cols-12">
            <div className="space-y-7 lg:col-span-8">
              <div className="space-y-4">
                <div className="aspect-video overflow-hidden rounded-[24px] bg-black shadow-[0_24px_60px_-30px_rgba(23,20,31,.55)] ring-1 ring-black/5">
                  <VideoPlayer
                    videoId={videoData.videoId}
                    startTime={playbackTime}
                  />
                </div>
                <div className="rounded-[20px] border border-[#e8e4dc] bg-white p-5">
                  <p className="mb-2 text-[10px] font-black tracking-[.18em] text-[#6d4aff]">NOW LEARNING</p>
                  <h1 className="mb-3 text-2xl font-extrabold leading-snug tracking-[-.03em]">{videoData.title}</h1>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                    <span className="rounded-full bg-[#f0edff] px-2.5 py-1 font-bold text-[#5b3ee4]">AI 분석 완료</span>
                    <span>•</span>
                    <span>{formatTime(videoData.duration)}</span>
                    <span>•</span>
                    <span>{videoData.chapters.length}개 챕터</span>
                  </div>
                </div>
              </div>

              {/* Tabs Section */}
              <Tabs defaultValue="summary" className="w-full">
                <div className="sticky top-[73px] z-30 overflow-x-auto rounded-2xl border border-[#e8e4dc] bg-[#fbfaf7]/95 p-1.5 shadow-sm backdrop-blur-xl">
                  <TabsList className="h-auto w-max justify-start gap-1 bg-transparent p-0">
                    <TabsTrigger
                      value="summary"
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 data-[state=active]:bg-[#17141f] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      강의 노트
                    </TabsTrigger>
                    <TabsTrigger
                      value="supplies"
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 data-[state=active]:bg-[#17141f] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      준비물 확인
                    </TabsTrigger>
                    <TabsTrigger
                      value="insights"
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 data-[state=active]:bg-[#17141f] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      실무 인사이트
                    </TabsTrigger>
                    <TabsTrigger
                      value="notes"
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 data-[state=active]:bg-[#17141f] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      내 기록
                    </TabsTrigger>
                    <TabsTrigger
                      value="english"
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-[#5b3ee4] data-[state=active]:bg-[#6d4aff] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      영어 흡수
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="summary" className="space-y-6 mt-8 animate-in fade-in-50 duration-500">
                  <div className="prose max-w-none">
                    <div className="mb-5"><p className="text-[10px] font-black tracking-[.18em] text-[#6d4aff]">CHAPTERS</p><h3 className="mt-1 text-2xl font-extrabold tracking-[-.03em]">핵심을 맥락으로 이어보세요</h3></div>
                    <div className="space-y-4 pr-2">
                      {videoData.chapters.map((chapter: GeminiChapter) => (
                        <ChapterSummary
                          key={chapter.id}
                          chapter={chapter}
                          isActive={currentChapter === chapter.id}
                          onChapterClick={setCurrentChapter}
                          onJumpToTime={handleJumpToTime}
                          onAddNote={handleAddNote}
                        />
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="supplies" className="mt-8 animate-in fade-in-50 duration-500">
                  {(videoData.tools?.length || videoData.materials?.length) ? (
                    <Card className="p-8 border-none bg-slate-50/50">
                      <div className="flex items-center gap-3 mb-8">
                        <span className="text-2xl">🛠️</span>
                        <div>
                          <h3 className="text-xl font-bold">준비물 및 도구</h3>
                          <p className="text-sm text-slate-500">강의를 수강하기 위해 필요한 항목들입니다.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {videoData.tools && videoData.tools.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900 border-b pb-2">필요 도구 (Tools)</h4>
                            <div className="flex flex-wrap gap-2">
                              {videoData.tools.map((tool: string, idx: number) => (
                                <span key={idx} className="bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg text-sm font-medium border border-blue-500/20">
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {videoData.materials && videoData.materials.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="font-semibold text-blue-900 border-b pb-2">필요 재료 (Materials)</h4>
                            <div className="flex flex-wrap gap-2">
                              {videoData.materials.map((material: string, idx: number) => (
                                <span key={idx} className="bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-lg text-sm font-medium border border-emerald-500/20">
                                  {material}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ) : (
                    <div className="text-center py-20 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                      <p className="text-slate-500">분석된 준비물 정보가 없습니다.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="insights" className="mt-8 animate-in fade-in-50 duration-500">
                  {insightData ? (
                    <InsightView analysis={insightData} videoId={videoData.videoId} />
                  ) : (
                    <div className="text-center py-20 bg-slate-50/50 rounded-xl border border-slate-100">
                      <div className="max-w-md mx-auto space-y-6">
                        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <span className="text-3xl">💡</span>
                        </div>
                        <h3 className="text-xl font-bold">전문가 수준의 깊이 있는 분석</h3>
                        <p className="text-slate-500 leading-relaxed">
                          단순 요약을 넘어, 영상 속에 숨겨진 <strong>실무 노하우, 핵심 원리, 제작자의 의도</strong>를 완벽하게 분석해드립니다.
                        </p>
                        <Button onClick={handleInsightAnalysis} disabled={isAnalyzingInsight} size="lg" className="w-full">
                          {isAnalyzingInsight ? 'AI가 열심히 분석 중입니다...' : '실무 인사이트 분석 시작하기'}
                        </Button>
                        {isAnalyzingInsight && <p className="text-xs text-slate-400">영상을 다시 읽고 있어 약 30~90초 정도 걸릴 수 있어요.</p>}
                        {insightError && <div className="rounded-xl bg-red-50 p-4 text-sm font-medium text-red-700">{insightError}<button onClick={handleInsightAnalysis} className="ml-2 underline">다시 시도</button></div>}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="mt-8 animate-in fade-in-50 duration-500">
                  <NotesEditor
                    videoId={videoData.videoId}
                    notes={notes}
                    onAddNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                    onJumpToTime={handleJumpToTime}
                    chapters={videoData.chapters}
                  />
                </TabsContent>

                <TabsContent value="english" className="mt-8 animate-in fade-in-50 duration-500">
                  <EnglishLearningView
                    videoId={videoData.videoId}
                    learning={videoData.languageLearning}
                    onJumpToTime={handleJumpToTime}
                  />
                </TabsContent>
              </Tabs>
            </div>

            {/* Right Sidebar (Curriculum) - 4/12 width */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-5">
                <Card className="overflow-hidden rounded-[22px] border-[#e8e4dc] bg-white shadow-none">
                  <div className="flex items-center justify-between border-b border-[#eeeae3] p-5 font-extrabold">
                    <span className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      커리큘럼
                    </span>
                    <span className="rounded-full bg-[#f0edff] px-2.5 py-1 text-xs font-bold text-[#5b3ee4]">{videoData.chapters.length} 챕터</span>
                  </div>
                  <div className="max-h-[58vh] space-y-1.5 overflow-y-auto p-3 custom-scrollbar">
                    {videoData.chapters.map((chapter: GeminiChapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => {
                          setCurrentChapter(chapter.id);
                          handleJumpToTime(chapter.startTime);
                        }}
                        className={`group flex w-full items-start gap-3 rounded-[14px] p-3 text-left text-sm transition-all ${currentChapter === chapter.id
                          ? 'bg-[#f0edff] font-bold text-[#5b3ee4]'
                          : 'text-slate-600 hover:bg-[#f7f5f0]'
                          }`}
                      >
                        <div className="mt-0.5 flex-shrink-0">
                          {currentChapter === chapter.id ? (
                            <Play className="h-4 w-4 fill-[#6d4aff] text-[#6d4aff]" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-slate-300 group-hover:border-slate-400 transition-colors" />
                          )}
                        </div>
                        <div>
                          <div className="line-clamp-2 leading-snug">{chapter.title}</div>
                          <div className="text-xs opacity-60 mt-1.5 font-normal flex items-center gap-1">
                            <span>{formatTime(chapter.startTime)}</span>
                            <span>-</span>
                            <span>{formatTime(chapter.endTime)}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>

                <Button
                  variant="outline"
                  className="w-full rounded-[16px] border-[#d9d3ca] bg-transparent py-6 font-bold text-slate-500 hover:bg-white hover:text-[#17141f]"
                  onClick={() => {
                    setVideoData(null);
                    setInsightData(null);
                    setCurrentChapter('');
                    setNotes([]);
                    setViewMode('chapters');
                  }}
                >
                  + 새로운 영상 분석하기
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
